import { useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { getConfig } from '../../shared/api/configApi';
import { runMenuAction } from '../menuActions';
import { pathForState } from '../definitions';
import log from '../../logger';

/**
 * `/main/` sends the admin to their home screen.
 *
 * This is `RootInitCtrl` plus `navigationService.navigateToFirstMenuItem`: read the config,
 * find the side-menu item flagged `isHomePage`, and go there. It is also what
 * `$urlRouterProvider.otherwise('/main/')` landed on, so an unmatched URL ends up here too.
 *
 * `replace` rather than a push, because `RootInitCtrl` redirected during the transition and
 * never left `/main/` in the history. Without it the browser back button bounces between the
 * home screen and this redirect.
 *
 * A tenant whose menu flags no home page renders nothing, which is what the Angular version
 * did: its loop simply found no match and returned. That is a config problem, and inventing a
 * fallback here would hide it.
 */
const HomeRedirect = () => {
  const navigate = useNavigate();

  useEffect(() => {
    let live = true;
    getConfig()
      .then((config) => {
        if (!live) return;
        const home = (config?.sideMenu || []).find((item) => item.isHomePage && item.navigation);
        if (!home) {
          log.debug('No side menu item is flagged isHomePage; staying put.');
          return;
        }
        // All three navigation shapes, because `navigateToFirstMenuItem` delegated to
        // `navigateToMenuItem`, which handled all three. A tenant whose menu opens with
        // Export Content or Preview App has a `clickHandler` home item, and handling only
        // `state` and `href` left it logging "No path for state undefined" and doing nothing.
        const { state, stateParams, href, clickHandler } = home.navigation;
        if (href) {
          window.location = href;
          return;
        }
        if (clickHandler) {
          runMenuAction(clickHandler);
          return;
        }
        const to = pathForState(state, stateParams);
        if (to) navigate({ to, replace: true });
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [navigate]);

  return null;
};

export default HomeRedirect;
